import type { ChangeEvent, FC } from 'react';
import { useCallback, useState } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';

import { Stack } from '@mui/material';

import { useFullTextSearchService } from 'api/data/services/projects/search/FullTextSearchService';
import { useProjectBasePath, useProjectBaseRoutePath } from 'app/routes/projects/hooks/useProjectPath';
import { SearchField } from 'components/inputs/SearchField/SearchField';
import { FULLTEXT_SEARCH_STATE } from 'state/fullTextSearch/fullTextSearchState';
import { SELECTED_PROJECT_STORE } from 'state/selectedProject/selectedProjectState';

export const FullTextSearchBar: FC = () => {
  const setSearchTerm = FULLTEXT_SEARCH_STATE((state) => state.setSearchTerm);
  const isLoading = FULLTEXT_SEARCH_STATE((state) => state.isLoading);

  const projectId = SELECTED_PROJECT_STORE((state) => state.projectId);
  const basePath = useProjectBasePath();
  const routePath = useProjectBaseRoutePath();

  const [value, setValue] = useState<string>('');

  const history = useHistory();
  const location = useLocation();

  const service = useFullTextSearchService();

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const onReset = () => {
    setValue('');
  };

  const onSearch = useCallback(async () => {
    if (projectId !== undefined && value.trim().length > 0) {
      setSearchTerm(value.trim());

      await service.search(projectId, value.trim());

      const isSearchPage = matchPath(location.pathname, { path: `${routePath}/search`, exact: false });

      if (!isSearchPage) {
        history.push(`${basePath}/search`);
      }
    }
  }, [projectId, value, setSearchTerm, service, location.pathname, routePath, history, basePath]);

  return (
    <Stack direction='row'>
      <SearchField
        onChange={onChange}
        onReset={onReset}
        onSearch={onSearch}
        placeholder='Search in project'
        disabled={isLoading}
        value={value}
        minWidth={240}
      />
    </Stack>
  );
};
